import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Loader2, FileText } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { fetchUploadedFilesRequest } from '@/store/slices/uploadedFilesSlice';

export default function UploadedFiles() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { files, isLoading, error } = useAppSelector((state) => state.uploadedFiles);

  useEffect(() => {
    dispatch(fetchUploadedFilesRequest());
  }, [dispatch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600';
      case 'PROCESSING':
        return 'text-yellow-600';
      case 'FAILED':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading files: {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Uploaded Files</h1>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>File ID</TableHead>
            <TableHead>Total Rows</TableHead>
            <TableHead>Processed Rows</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Upload Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files?.map((file) => (
            <TableRow key={file.id}>
              <TableCell className="font-mono">
              <Button
                  variant="ghost"
                  className="text-blue-600"
                  onClick={() => navigate(`/file-details/${file.id}`)}
                >
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  {file.id}
                  </Button>
                </TableCell>
              <TableCell>{file.row_count}</TableCell>
              <TableCell>{file.processed_rows}</TableCell>
              <TableCell>
                <span className={`font-semibold ${getStatusColor(file.status)}`}>
                  {file.status}
                </span>
              </TableCell>
              <TableCell>
                {format(new Date(file.created), 'PPp')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}